import { DEFAULT_DEBOUNCE_DELAY, STATUSPAGE } from '@repo/ui';
import debounce from 'debounce';
import { GetAllStudentsUseCaseInterface } from 'domain/usecases';
import { ORDER_TYPE } from 'domain/value-objects/order-type';
import { PERSONS_ORDER_COLUMN } from 'domain/value-objects/persons-order-column';
import { StudentData } from 'domain/value-objects/student-data';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearStudentsListData,
  loadStudentsListData,
  loadStudentsListFilters,
} from 'presentation/config/store/persons/students/student-list-slice';
import { useCallback, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { useDispatch } from 'react-redux';

import { IStudentsListFilters } from '../models/students-list-filters';

interface IPaginationMetadata {
  selectedPage: number;
  rowsPerPage: number;
  orderColumn: PERSONS_ORDER_COLUMN;
  sortDirection: ORDER_TYPE;
  searchString: string;
}

export const useStudentsList = () => {
  // Hooks
  const { studentService } = useService();
  const dispatch = useDispatch();

  // States
  const [paginationMetadata, setPaginationMetadata] =
    useState<IPaginationMetadata>({
      selectedPage: 0,
      rowsPerPage: 10,
      orderColumn: PERSONS_ORDER_COLUMN.NAME,
      sortDirection: ORDER_TYPE.ASC,
      searchString: '',
    });
  const [listLoadingStatus, setListLoadingStatus] = useState(
    STATUSPAGE.LOADING,
  );
  const { studentsListData, studentsListFilters } = useAppSelector(
    (state) => state.studentsList,
  );

  // Methods
  const changePaginationMetadata = <T extends keyof IPaginationMetadata>(
    key: T,
    value: IPaginationMetadata[T],
  ) => {
    setPaginationMetadata((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handlePageChange = useCallback((page: number) => {
    // setListLoadingStatus(STATUSPAGE.LOADING);
    changePaginationMetadata('selectedPage', page - 1);
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    // setListLoadingStatus(STATUSPAGE.LOADING);
    changePaginationMetadata('rowsPerPage', rowsPerPage);
  }, []);

  const handleSortRemote = useCallback(
    (selectedColumn: TableColumn<StudentData>, sortDirection: SortOrder) => {
      // setListLoadingStatus(STATUSPAGE.LOADING);
      changePaginationMetadata(
        'orderColumn',
        selectedColumn.sortField as PERSONS_ORDER_COLUMN,
      );
      changePaginationMetadata(
        'sortDirection',
        sortDirection.toUpperCase() as ORDER_TYPE,
      );
      changePaginationMetadata('selectedPage', 0);
    },
    [],
  );

  const handleSearchRemote = useCallback((searchString: string) => {
    // setListLoadingStatus(STATUSPAGE.LOADING);
    changePaginationMetadata('searchString', searchString);
  }, []);

  const requestGetAllStudents = useCallback(
    async (params: GetAllStudentsUseCaseInterface.Params) => {
      return studentService
        .getAllStudents(params)
        .then((res) => {
          dispatch(loadStudentsListData(res));
        })
        .catch((err) => {
          dispatch(clearStudentsListData());
          notifyError(err.message);
        })
        .finally(() => {
          setListLoadingStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const debouncedRequestGetAllStudents = useMemo(
    () =>
      debounce((valuesToSubmit: GetAllStudentsUseCaseInterface.Params) => {
        setListLoadingStatus(STATUSPAGE.LOADING);
        requestGetAllStudents(valuesToSubmit);
      }, DEFAULT_DEBOUNCE_DELAY),

    [],
  );

  const removeStudentsListData = useCallback(() => {
    dispatch(clearStudentsListData());
  }, []);

  const saveStudentsListFilters = useCallback(
    (params: IStudentsListFilters) => {
      dispatch(loadStudentsListFilters(params));
    },
    [],
  );

  return {
    // States
    studentsListData,
    studentsListFilters,
    paginationMetadata,
    debouncedRequestGetAllStudents,
    listLoadingStatus,
    // Methods
    saveStudentsListFilters,
    setListLoadingStatus,
    requestGetAllStudents,
    removeStudentsListData,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortRemote,
    handleSearchRemote,
  };
};
